import { HttpClient } from '@angular/common/http';
import { APP_INITIALIZER, ErrorHandler, inject, Injectable, isDevMode, NgModule } from '@angular/core';
import { MatSnackBarModule } from '@angular/material/snack-bar';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { ServiceWorkerModule } from '@angular/service-worker';
import { AuthClientConfig } from '@auth0/auth0-angular';
import { CorrelationIdModule } from '@frontend/common/correlation-id';
import { InterceptorModule } from '@frontend/common/interceptor';
import { LoadingModule } from '@frontend/common/loading';
import {
  PhAuthModule,
  PhAuthServiceConfig,
  PH_AUTH_SERVICE_CONFIG,
  PhAuthService
} from '@frontend/common/ph-auth';
import {
  PhConfigLoaderModel,
  PhConfigLoaderModule,
  PhConfigLoaderService,
} from '@frontend/common/ph-config-loader';
import {
  CustomSerializer,
  RouterEffects,
  routerReducerInitialState,
  RouterStateUrl,
} from '@frontend/common/ph-router-store';
import { UpdaterConfig, UpdaterModule } from '@frontend/common/updater';
import { setupSentry } from '@frontend/common/util';
import { EffectsModule } from '@ngrx/effects';
import * as fromRouter from '@ngrx/router-store';
import {
  FullRouterStateSerializer,
  RouterStateSerializer,
  StoreRouterConnectingModule,
} from '@ngrx/router-store';
import { ActionReducerMap, StoreModule } from '@ngrx/store';
import { StoreDevtoolsModule } from '@ngrx/store-devtools';
import * as Sentry from '@sentry/angular';
import { provideApollo } from 'apollo-angular';
import { combineLatest, Observable, of } from 'rxjs';
import { catchError, map, tap } from 'rxjs/operators';
import { environment } from '../environments/environment';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { DisplayBoardGuard } from './display-board-config.guard';
import * as fromDisplayBoardConfig from './display-board-config.reducer';
import { DisplayBoardConfigService } from './display-board-config.service';
import { DisplayBoardExpectedRouteService } from './display-board-expected-route.service';
import { DisplayBoardTypeRedirectService } from './display-board-type-redirect.service';
import { GraphQlGuard } from './graphql.guard';
import { InMemoryCache } from '@apollo/client/cache';
import { setContext } from '@apollo/client/link/context';
import { ApolloLink } from '@apollo/client/core';
import { GraphQLWsLink } from '@apollo/client/link/subscriptions';
import { createClient } from 'graphql-ws';
@Injectable()
export class PhConfigLoaderFactory {
  constructor(private http: HttpClient) {}
  cb = (config: PhConfigLoaderModel): Observable<PhConfigLoaderModel> =>
    this.http.get<{ id: number; auth_org_id: string; auth_device_client_id: string; locale: string }>(
      config.APP_API_URL + 'customers/id'
    ).pipe(
      map(
        (customer) => customer
      ),
      catchError(() =>
        of({
          id: 'disabled',
          auth_org_id: null,
          auth_device_client_id: null,
          locale: null,
        })
      ),
      map((customer) => ({
        ...config,
        CUSTOMER_ID: customer.id,
        AUTH_ORG_ID: customer.auth_org_id,
        AUTH_DEVICE_CLIENT_ID: customer.auth_device_client_id,
        LOCALE: customer.locale,
      })),
      tap(({ SENTRY_DSN }: any) => {
        if (!isDevMode()) {
          setupSentry(
            SENTRY_DSN,
            window.location.href,
            environment.version,
            'display-board'
          );
        }
      })
    );
}

export interface State {
  routerReducer: fromRouter.RouterReducerState<RouterStateUrl>;
  displayBoardConfig: fromDisplayBoardConfig.State;
}

export const reducers: ActionReducerMap<State> = {
  routerReducer: fromRouter.routerReducer,
  [fromDisplayBoardConfig.featureKey]: fromDisplayBoardConfig.reducer as any,
};

@NgModule({
  declarations: [AppComponent],
  imports: [
    BrowserModule,
    BrowserAnimationsModule,
    StoreModule.forRoot(reducers, {
      metaReducers: [],
      initialState: { routerReducer: routerReducerInitialState },
      runtimeChecks: { strictStateImmutability: true, strictActionImmutability: true },
    }),
    EffectsModule.forRoot([RouterEffects]),
    AppRoutingModule,
    StoreRouterConnectingModule.forRoot({ serializer: FullRouterStateSerializer }),
    PhConfigLoaderModule.forRoot(PhConfigLoaderFactory),
    environment.production ? [] : StoreDevtoolsModule.instrument({ maxAge: 100 , logOnly: !isDevMode(), connectInZone: true}),
    PhAuthModule,
    CorrelationIdModule, // should not be required by InterceptorModule
    InterceptorModule,
    MatSnackBarModule,
    ServiceWorkerModule.register('ngsw-worker.js', { enabled: environment.production }),
    UpdaterModule.forRoot(
      [PhConfigLoaderService],
      (appConfig: PhConfigLoaderService): UpdaterConfig => ({
        checkInterval: appConfig.getConfig('CHECK_RELEASE_AFTER_SEC'),
      })
    ),
    LoadingModule,
  ],
  providers: [
    GraphQlGuard,
    provideApollo(() => {
      const phAuthSrv: PhAuthService = inject(PhAuthService);
      const config: PhConfigLoaderService = inject(PhConfigLoaderService);
      const authContext = setContext(() => {
        const token = phAuthSrv.token;
        return {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        };
      });

      const wsUrl = config.getConfig('GRAPHQL_URL');
      const wsHttpUrl = wsUrl.startsWith('https')
        ? wsUrl.replace('https://', 'wss://')
        : wsUrl.replace('http://', 'ws://');

      const wsClient = createClient({
        url: wsHttpUrl,
        connectionParams: () => {
          const token = phAuthSrv.token;
          return {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          };
        },
      });

      const wsLink = new GraphQLWsLink(wsClient);

      return {
        link: ApolloLink.from([authContext, wsLink]),
        cache: new InMemoryCache(),
      };
    }),
    {
      provide: APP_INITIALIZER,
      deps: [PhConfigLoaderService, AuthClientConfig],
      useFactory: (ac: PhConfigLoaderService) => () => ac.load(),
      multi: true,
    },
    { provide: RouterStateSerializer, useClass: CustomSerializer },
    {
      provide: ErrorHandler,
      useFactory: () => {
        if (environment.production) {
          return Sentry.createErrorHandler();
        }
        return new ErrorHandler();
      },
    },
    {
      provide: PH_AUTH_SERVICE_CONFIG,
      useFactory: phAuthServiceFactory,
      deps: [PhConfigLoaderService],
    },
    DisplayBoardGuard,
    DisplayBoardConfigService,
    DisplayBoardTypeRedirectService,
    DisplayBoardExpectedRouteService,
  ],
  bootstrap: [AppComponent],
})
export class AppModule {}

function phAuthServiceFactory(configSrv: PhConfigLoaderService): PhAuthServiceConfig {
  return combineLatest({
    APP_API_URL: configSrv.getConfig$('APP_API_URL'),
    AUTH_DOMAIN: configSrv.getConfig$('AUTH_DOMAIN'),
    AUTH_AUDIENCE: configSrv.getConfig$('AUTH_AUDIENCE'),
    AUTH_DEVICE_CLIENT_ID: configSrv.getConfig$('AUTH_DEVICE_CLIENT_ID'),
    REFRESH_TOKEN_IN_ADVANCE_SEC: configSrv.getConfig$('REFRESH_TOKEN_IN_ADVANCE_SEC'),
    AUTH_LOGOUT_CALLBACK_URL: configSrv.getConfig$('AUTH_LOGOUT_CALLBACK_URL'),
    AUTH_ORG_ID: configSrv.getConfig$('AUTH_ORG_ID'),
    LOCALE: configSrv.getConfig$('LOCALE'),
  }).pipe(
    map(({
      APP_API_URL,
      AUTH_DOMAIN,
      AUTH_AUDIENCE,
      AUTH_DEVICE_CLIENT_ID,
      REFRESH_TOKEN_IN_ADVANCE_SEC,
      AUTH_LOGOUT_CALLBACK_URL,
      AUTH_ORG_ID,
      LOCALE,
    }) => ({
      APP_API_URL,
      AUTH_DOMAIN,
      AUTH_AUDIENCE,
      AUTH_DEVICE_CLIENT_ID,
      REFRESH_TOKEN_IN_ADVANCE_SEC,
      AUTH_LOGOUT_CALLBACK_URL,
      AUTH_ORG_ID,
      LOCALE,
    }))
  );
}
